import { useCallback, useRef, useState } from 'react';
import type { StoryElement } from '../types';
import { isRunFailed, isRunFinished } from '../utils/savedSearchRunState';
import type { SavedSearchRunState } from '../utils/savedSearchRunState';
import { buildSearchPollPoint } from '../utils/searchFilters';
import type { SearchPollPoint, SearchRunProgress } from '../utils/searchFilters';
import type { SearchTimingDetails } from '../repositories/savedSearchRepository';
import { runWindowFromStatus } from '../utils/searchWindow';
import type { SearchRunWindow } from '../utils/searchWindow';

/**
 * Single owner of the "follow a search run to completion" protocol.
 *
 * Every kind of run the reader starts — a saved search, an ad-hoc search, a
 * TLDR preview, a deepen pass — is followed the same way: poll its status
 * endpoint on the latency-aware cadence below and, on each poll, apply the shared
 * effects in a fixed order (title, judged/candidate progress, poll-timeline
 * point, provisional articles), then throw if the run FAILED or stop once it
 * FINISHED. Before this owner existed the loop was copy-pasted per run kind
 * and additions kept landing in some copies but not others: the poll
 * timeline shipped in two of ReaderPage's four loops, so TLDR previews and
 * deepen passes kept displaying the previous run's frozen timeline.
 */

export const RUN_POLL_INTERVAL_MS = 250;
export const RUN_INITIAL_FAST_POLL_AT_MS = 150;
export const RUN_FAST_POLL_INTERVAL_MS = 50;
export const RUN_FAST_POLL_UNTIL_MS = 1000;

export function runPollDelayMs(elapsedMs: number): number {
  if (elapsedMs < RUN_INITIAL_FAST_POLL_AT_MS) {
    return RUN_INITIAL_FAST_POLL_AT_MS - elapsedMs;
  }
  if (elapsedMs < RUN_FAST_POLL_UNTIL_MS) {
    return RUN_FAST_POLL_INTERVAL_MS;
  }
  return RUN_POLL_INTERVAL_MS;
}

/**
 * The slice of a run-status payload the protocol consumes. The saved-search
 * and TLDR-preview status endpoints serve the same backend payload
 * (`build_search_status_response` in `saved_search_helpers.py`), so every
 * followed run carries these fields.
 */
export interface FollowableRunStatus {
  title?: string | null;
  status: SavedSearchRunState;
  articles: StoryElement[];
  judged_count: number;
  candidate_count: number;
  cached_relevant_count: number;
  live_relevant_count: number;
  live_not_relevant_count: number;
  error?: string | null;
  /** Set by a run that finished with results but did not judge everything. */
  warning?: string | null;
  /** the lookback its candidate retrieval used, in hours. */
  window_hours?: number | null;
  /** The run's start instant — the anchor `window_hours` was measured from. */
  created_at?: string | null;
  timing_details?: SearchTimingDetails | null;
}

/** Where each poll's shared observations land (state setters in production). */
export interface FollowRunEffects<S extends FollowableRunStatus> {
  onTitle: (title: string) => void;
  onProgress: (progress: SearchRunProgress) => void;
  recordPoll: (status: S) => void;
  onArticles: (articles: StoryElement[]) => void;
  /**
   * The window this run's results were retrieved under, or null for a run that
   * does not report one (every run that predates the field). It rides the shared
   * protocol rather than each caller's own `onPoll` for the reason this owner
   * exists at all: the reader starts runs four different ways, and a per-caller
   * copy is how the 24h/72h control ended up reading stale composer state.
   */
  onRunWindow: (window: SearchRunWindow | null) => void;
}

export interface FollowRunOptions<S extends FollowableRunStatus> {
  fetchStatus: () => Promise<S>;
  /**
   * False once a newer request supersedes this one; checked before and after
   * every fetch so a stale run never touches the pane.
   */
  isCurrent: () => boolean;
  /** Message for a failed run whose status carries no server error. */
  errorLabel: string;
  /** Caller-specific per-poll side effect, applied after the shared ones. */
  onPoll?: (status: S) => void;
  /**
   * How much of a run to show before it finishes.
   *
   * - `'finished'`: nothing until FINISHED. Keeps a committed result set on
   *   screen while a widening search assembles a larger candidate set.
   * - `'each-poll'`: every snapshot, verbatim. For a restored run, whose first
   *   snapshot is already a stable prior result set.
   * - `'streaming'`: every snapshot, but additively — a row that has been shown
   *   is never taken away mid-run, and the FINISHED snapshot is authoritative.
   *   This is what a fresh run wants. The server publishes its cached verdicts
   *   before it dispatches a single judge call (``update_progress`` in
   *   fast_search_runner), so a warm search has a usable answer in ~300ms while
   *   the live wave runs for seconds; under `'finished'` all of it was fetched,
   *   ignored, and re-fetched at the end. The reason fresh snapshots were not
   *   published verbatim is real — a row can leave a snapshot when topic
   *   clustering merges it into a neighbour — so mid-run publications only ever
   *   add.
   */
  publishResults?: 'each-poll' | 'finished' | 'streaming';
  /** Injectable for tests; production uses a real setTimeout delay. */
  sleep?: (ms: number) => Promise<void>;
  /** Injectable monotonic clock for cadence tests. */
  now?: () => number;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

/**
 * The next list to show for a streaming run: this snapshot in the server's
 * ranking, followed by any already-shown row the snapshot no longer carries.
 *
 * Mutates ``shown`` to record what was published. Ranking comes from the server
 * on every poll, so a late high-scoring result still lands in its right place;
 * the retained tail exists only so a row the reader is looking at cannot vanish
 * mid-run when topic clustering folds it into a neighbour. The finished
 * snapshot is applied verbatim by the caller, which is where such a merge
 * finally takes effect.
 */
export function mergeStreamedArticles(
  shown: Map<string, StoryElement>,
  snapshot: StoryElement[],
): StoryElement[] {
  const inSnapshot = new Set(snapshot.map((article) => article.id));
  const retained = [...shown.values()].filter((article) => !inSnapshot.has(article.id));
  shown.clear();
  for (const article of [...snapshot, ...retained]) shown.set(article.id, article);
  return [...snapshot, ...retained];
}

/**
 * The protocol itself, with its effects injected. Exported for tests;
 * components should go through `useSearchRunFollower`.
 *
 * Resolves with the FINISHED status, or undefined if the run was superseded
 * mid-flight. Throws when the run reports FAILED.
 */
export async function followRunToCompletion<S extends FollowableRunStatus>(
  effects: FollowRunEffects<S>,
  {
    fetchStatus,
    isCurrent,
    errorLabel,
    onPoll,
    publishResults = 'finished',
    sleep = defaultSleep,
    now = () => performance.now(),
  }: FollowRunOptions<S>,
): Promise<S | undefined> {
  const startedAt = now();
  // Rows already shown to the reader, newest snapshot's version of each, in the
  // order they were first published. Only 'streaming' fills it.
  const shown = new Map<string, StoryElement>();
  while (isCurrent()) {
    const status = await fetchStatus();
    if (!isCurrent()) return undefined;
    if (status.title) effects.onTitle(status.title);
    effects.onProgress({ judged: status.judged_count, candidates: status.candidate_count });
    effects.recordPoll(status);
    const finished = isRunFinished(status.status);
    if (publishResults === 'each-poll' || finished) {
      // The finished snapshot is authoritative: it may drop a row that earlier
      // snapshots carried, which is exactly when a merge is safe to apply.
      effects.onArticles(status.articles);
      effects.onRunWindow(runWindowFromStatus(status));
    } else if (publishResults === 'streaming') {
      effects.onArticles(mergeStreamedArticles(shown, status.articles));
      effects.onRunWindow(runWindowFromStatus(status));
    }
    onPoll?.(status);
    if (isRunFailed(status.status)) {
      throw new Error(status.error || errorLabel);
    }
    if (isRunFinished(status.status)) {
      // Incomplete coverage is a server-side problem, not the reader's: it
      // alarms through CloudWatch (SearchIncompleteCoverage) rather than
      // interrupting a run whose results are on screen and usable.
      return status;
    }
    await sleep(runPollDelayMs(now() - startedAt));
  }
  return undefined;
}

interface SearchPollCounters {
  cachedRelevant: number;
  liveRelevant: number;
  liveNotRelevant: number;
  complete: boolean;
}

/**
 * Owns the state a followed run streams into the pane: the judged/candidate
 * progress readout and the poll timeline. `onTitle` / `onArticles` say where
 * a poll's title and provisional articles land and must be referentially
 * stable (pass state setters).
 */
export function useSearchRunFollower({ onTitle, onArticles, onRunWindow }: {
  onTitle: (title: string) => void;
  onArticles: (articles: StoryElement[]) => void;
  onRunWindow: (window: SearchRunWindow | null) => void;
}) {
  const [runProgress, setRunProgress] = useState<SearchRunProgress | null>(null);
  const [pollTimeline, setPollTimeline] = useState<SearchPollPoint[]>([]);
  const [timingDetails, setTimingDetails] = useState<SearchTimingDetails | null>(null);
  const pollStartedAtRef = useRef(0);
  const previousPollCountersRef = useRef<SearchPollCounters | undefined>(undefined);

  /**
   * Clear the progress readout and start a fresh poll timeline. Call once per
   * run, before the first poll — including runs that keep the previous
   * results on screen (deepen), so the timeline never shows a previous run.
   */
  const beginRun = useCallback(({ keepRunWindow = false } = {}) => {
    setRunProgress(null);
    // Normally the previous run's window must not outlive its results. A
    // stale-while-revalidate transition explicitly keeps both together until
    // the pending run atomically publishes its replacement.
    if (!keepRunWindow) onRunWindow(null);
    pollStartedAtRef.current = performance.now();
    previousPollCountersRef.current = undefined;
    setPollTimeline([]);
    setTimingDetails(null);
  }, [onRunWindow]);

  const recordPoll = useCallback((status: FollowableRunStatus) => {
    const counters = {
      cachedRelevant: status.cached_relevant_count,
      liveRelevant: status.live_relevant_count,
      liveNotRelevant: status.live_not_relevant_count,
      complete: isRunFinished(status.status),
    };
    const point = buildSearchPollPoint(
      performance.now() - pollStartedAtRef.current,
      counters,
      previousPollCountersRef.current,
    );
    previousPollCountersRef.current = counters;
    setPollTimeline((previous) => [...previous, point]);
    if (status.timing_details) setTimingDetails(status.timing_details);
  }, []);

  const followRun = useCallback(<S extends FollowableRunStatus>(
    options: Omit<FollowRunOptions<S>, 'sleep'>,
  ): Promise<S | undefined> => followRunToCompletion(
    { onTitle, onProgress: setRunProgress, recordPoll, onArticles, onRunWindow },
    options,
  ), [onTitle, onArticles, onRunWindow, recordPoll]);

  return { runProgress, pollTimeline, timingDetails, beginRun, followRun };
}
